import LocationAutocomplete from '@components/LocationAutocomplete'
import { useSortableRowSensors } from '@components/SortableRow'
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { GooglePlace, MAX_LOCATION_FULL_TEXT_LENGTH, placeTextProblem } from '@lib/place'
import React, { useState } from 'react'
import {
	CreateTripDraft,
	addDestination,
	canAddDestination,
	decrementDays,
	incrementDays,
	moveDestination,
	removeDestination,
} from './core/draft'
import { placeFromFreeText, placeFromGooglePlace } from './core/place'
import { stepTitle } from './core/presentation'
import DestinationRow, { DestinationRowDetail } from './DestinationRow'
import { Heading } from './shared.styles'
import {
	AddButton,
	AddError,
	AddField,
	AddRow,
	DestinationList,
	Intro,
	LimitHint,
	ListNotice,
} from './WhereStep.styles'

interface WhereStepProps {
	draft: CreateTripDraft
	onChange: (draft: CreateTripDraft) => void
	/** Per-row meta and error lines by row key — the edit flow's event counts and conflicts. */
	rowDetails?: Record<string, DestinationRowDetail>
	/** A notice above the list for what no row can carry — a removed destination that still holds events. */
	listNotice?: string | null
	/** Called with the row's `removeRefusal` instead of removing it — the edit flow toasts it. */
	onRemoveRefused?: (message: string) => void
}

/**
 * Step 1 — the flow: add places in visit order, each with a day count. The
 * search row is always there; picking a place — a Google match, or the
 * search's trailing "use it as typed" row for somewhere it doesn't know —
 * appends it and clears the field for the next one. Rows reorder by their
 * drag handle; the trip's length builds up in the bottom bar.
 */
const WhereStep: React.FC<WhereStepProps> = ({
	draft,
	onChange,
	rowDetails,
	listNotice,
	onRemoveRefused,
}) => {
	const [searchText, setSearchText] = useState('')
	const atLimit = !canAddDestination(draft)
	// The contract's rule on the typed text, applied as it is typed: blank just
	// keeps Add quiet, too long says so under the field before the server can.
	const textProblem = placeTextProblem(searchText)

	const sensors = useSortableRowSensors()

	const rowKeys = draft.destinations.map(destination => destination.key)

	const addPlace = (googlePlace: GooglePlace) => {
		onChange(addDestination(draft, placeFromGooglePlace(googlePlace)))
		setSearchText('')
	}

	// The search's own custom row and Enter both land here; so does the button.
	const addFreeText = (text: string) => {
		if (placeTextProblem(text) !== null || atLimit) return
		onChange(addDestination(draft, placeFromFreeText(text.trim())))
		setSearchText('')
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') setSearchText('')
	}

	// A row still holding events stays put and says why, rather than leaving
	// the list to explain a removal that can't be saved.
	const removeRow = (index: number) => {
		const refusal = rowDetails?.[draft.destinations[index].key]?.removeRefusal
		if (refusal) {
			onRemoveRefused?.(refusal)
			return
		}
		onChange(removeDestination(draft, index))
	}

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return
		onChange(
			moveDestination(draft, rowKeys.indexOf(String(active.id)), rowKeys.indexOf(String(over.id))),
		)
	}

	return (
		<div>
			<Heading as="h2">{stepTitle('where')}</Heading>
			<Intro>
				We recommend organizing your trips by adding in the cities. But anywhere else works as
				well like by Country, parks, etc.
			</Intro>

			<AddRow onKeyDown={handleKeyDown}>
				<AddField>
					<LocationAutocomplete
						id="create-trip-place-search"
						ariaLabel="Add a destination"
						value={searchText}
						onChange={setSearchText}
						// A new trip has no places of its own to offer, so every pick is a Search result.
						onPlaceSelect={place => place.googlePlace && addPlace(place.googlePlace)}
						onFreeTextSelect={addFreeText}
						placeholder="City, country, park…"
						autoFocus
					/>
				</AddField>
				<AddButton
					type="button"
					disabled={textProblem !== null || atLimit}
					onClick={() => addFreeText(searchText)}
				>
					Add Destination
				</AddButton>
			</AddRow>
			{textProblem === 'tooLong' && (
				<AddError role="alert">
					Keep a place name to {MAX_LOCATION_FULL_TEXT_LENGTH} characters or fewer.
				</AddError>
			)}
			{atLimit && (
				<LimitHint>This trip is at its limit — remove a place or trim some days to add more.</LimitHint>
			)}

			{listNotice && <ListNotice role="alert">{listNotice}</ListNotice>}
			{draft.destinations.length > 0 && (
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
						<DestinationList $afterNotice={!!listNotice}>
							{draft.destinations.map((destination, index) => (
								<DestinationRow
									key={destination.key}
									destination={destination}
									meta={rowDetails?.[destination.key]?.meta}
									error={rowDetails?.[destination.key]?.error}
									onIncrement={() => onChange(incrementDays(draft, index))}
									onDecrement={() => onChange(decrementDays(draft, index))}
									onRemove={() => removeRow(index)}
								/>
							))}
						</DestinationList>
					</SortableContext>
				</DndContext>
			)}
		</div>
	)
}

export default WhereStep
